<template>
  <div>
    <BaseDialog centered contentClass="add-opportunity" ref="dialog" persistent :show-actions="true">
      <template slot="title">
        <div class="title">
          {{ $t('page.opportunities.new.title') }}
        </div>
        <div class="subtitle">
          {{ $t('page.opportunities.new.subtitle') }}
        </div>
      </template>
      <template slot="content">
        <div class="add-opportunity__form">
          <form @submit.prevent="onSubmit" novalidate>
            <FormInput
              ref="opportunityNameInput"
              :label="$t('page.opportunities.new.name')"
              name="opportunityName"
              type="text"
              hasGrayBackground
              v-model="newOpportunity.name"
              :readonly="isSubmitting"
              is-required
              :error="nameError"
              v-validate="'required'"
            />
            <FormInput
              ref="emitterInput"
              :label="$t('page.opportunities.new.emitter')"
              name="opportunityEmitter"
              type="text"
              hasGrayBackground
              v-model="newOpportunity.emitter"
              :readonly="isSubmitting"
              is-required
              :error="emitterError"
              v-validate="'required'"
            />
            <BaseSelect
              name="gridTemplateSelection"
              :label="$t('page.opportunities.new.grid-template')"
              v-model="newOpportunity.evaluationGridTemplateId"
              :items="gridTemplateOptions"
              item-text="name"
              item-value="value"
              :is-disabled="isSubmitting"
              @change="onGridTemplateChange"
              is-required
            />
            <BaseSelect
              name="sourceSelection"
              :label="$t('page.opportunities.new.source')"
              v-model="newOpportunity.source"
              :items="sourcesTypes"
              item-text="name"
              item-value="value"
              :is-disabled="isSubmitting"
              @change="onSourceChange"
            />
            <div class="dates">
              <FormDateRangePicker
                ref="dates"
                name="datePicker"
                :label="$t('page.opportunities.new.dates')"
                :values="dates"
                :error="datesError"
                v-validate="'length:2'"
                v-model="dates"
                :disabled="isSubmitting"
                @updated-dates="onDatesChange"
                is-required
              />
            </div>
          </form>
          <div class="add-opportunity__documents" v-if="tenantHasDocumentsAccess()">
            <div class="upload-title">{{ $t('page.opportunities.new.documents') }}</div>
            <div v-if="documents.length > 0">
              <BaseDocumentTag
                class="add-opportunity__document"
                icon="file-text"
                v-for="document in documents"
                :key="document.name"
                :document="document"
                actionable
                :show-delete="!isSubmitting"
                @delete-document="deleteOpportunityDocument"
                @download-document="downloadDocument"
              />
              <FileUpload minimalist multiple icon="file-magnify" :disabled="isSubmitting" @add-files="addFiles" />
            </div>
            <FileUpload v-else multiple icon="file-magnify" :disabled="isSubmitting" @add-files="addFiles" />
          </div>
        </div>
      </template>
      <template slot="actions">
        <form @submit.prevent="onSubmit">
          <BaseButton is-outlined :disabled="isSubmitting" @click.native="closeDialog">
            <span>{{ $t('actions.cancel') }}</span>
          </BaseButton>
          <FormButton type="submit" :disabled="!isFormValid" :is-loading="isSubmitting">{{ submitLabel }}</FormButton>
        </form>
      </template>
    </BaseDialog>
    <ConfirmationDialog ref="dialogDeleteDocument" :buttonText="$t('actions.delete')" @execute="executeDeleteDocument">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="page.opportunity.delete-document-confirmation">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
            <template v-slot:documentName v-if="manipulatedObject">
              <span class="confirmation-dialog__user"> {{ manipulatedObject.name }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import BaseDialog from '@/components/base/BaseDialog';
import BaseDocumentTag from '@/components/base/BaseDocumentTag.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog.vue';
import FormButton from '@/components/form/FormButton.vue';
import FormDateRangePicker from '@/components/form/FormDateRangePicker';
import FormInput from '@/components/form/FormInput.vue';
import FileUpload from '@/components/form/FileUpload.vue';

import { mapActions, mapGetters } from 'vuex';
import { isXlsFile } from '@/util/document-file-util';
import { tenantHasDocumentsAccess, tenantHasSlipExtractionAccess } from '@/util/module-access-util';
import { formatDate, toMoment } from '@/util/date-util';
import { downloadBlob } from '@/util/download-file';

export default {
  name: 'AddOpportunityDialog',
  components: {
    BaseButton,
    BaseDialog,
    BaseDocumentTag,
    BaseSelect,
    ConfirmationDialog,
    FormButton,
    FormDateRangePicker,
    FormInput,
    FileUpload,
  },
  data() {
    return {
      manipulatedObject: null,
      isSubmitting: false,
      isUploadingDocuments: false,
      dates: [],
      newOpportunity: {
        tenderId: null,
        name: '',
        emitter: '',
        evaluationGridTemplateId: null,
        statusId: null,
        source: 'external',
        startDate: null,
        endDate: null,
        notes: null,
      },
      documents: [],
      sourcesTypes: [
        {
          name: `${this.$t('page.opportunities.new.sources.external')}`,
          value: 'external',
        },
        {
          name: `${this.$t('page.opportunities.new.sources.invitation')}`,
          value: 'invitation',
        },
        {
          name: `${this.$t('page.opportunities.new.sources.private')}`,
          value: 'private',
        },
        {
          name: `${this.$t('page.opportunities.new.sources.others')}`,
          value: 'others',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('evaluationGridTemplates', ['evaluationGridTemplates']),
    gridTemplateOptions() {
      // With no template to name, a null id still lets the API apply its own default
      if (!this.evaluationGridTemplates.length) {
        return [{ name: this.$t('page.opportunities.new.default-grid-template'), value: null }];
      }

      return this.evaluationGridTemplates.map((template) => ({ name: template.name, value: template.id }));
    },
    // Mirrors EvaluationGridTemplate.pickDefault on the API side: the primary
    // template, else the first one
    defaultGridTemplateId() {
      const templates = this.evaluationGridTemplates;
      if (!templates.length) return null;

      const primary = templates.find((template) => template.isPrimary);
      return (primary ?? templates[0]).id;
    },
    submitLabel() {
      return this.isUploadingDocuments ? this.$t('page.opportunities.new.uploading-documents') : this.$t('actions.add');
    },
    isFormValid() {
      return (
        this.newOpportunity.name &&
        this.newOpportunity.emitter &&
        this.dates.length === 2 &&
        this.newOpportunity.startDate &&
        this.newOpportunity.endDate
      );
    },
    nameError() {
      const error = this.errors.items.find((x) => x.field === 'opportunityName');
      return error ? error.msg : undefined;
    },
    emitterError() {
      const error = this.errors.items.find((x) => x.field === 'opportunityEmitter');
      return error ? error.msg : undefined;
    },
    datesError() {
      const dateError = this.errors.items.find((x) => x.field === 'datePicker');

      return dateError ? dateError.msg : undefined;
    },
  },
  methods: {
    ...mapActions('opportunities', ['addOpportunity']),
    ...mapActions('opportunityDocuments', ['addDocuments']),
    ...mapActions('evaluationGridTemplates', ['getEvaluationGridTemplates']),
    tenantHasDocumentsAccess,
    openDialog() {
      this.$refs.dialog.openModal();
      this.loadEvaluationGridTemplates();

      this.$nextTick(() => {
        const refInput = this.$refs.opportunityNameInput.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    loadEvaluationGridTemplates() {
      this.getEvaluationGridTemplates()
        .then(() => {
          // The list arrives after the dialog is open, so preselect then — but never
          // over a choice the user has already made
          if (this.newOpportunity.evaluationGridTemplateId === null) {
            this.newOpportunity.evaluationGridTemplateId = this.defaultGridTemplateId;
          }
        })
        .catch(() => {
          // Keep the dialog usable: a null id still applies the tenant's default
        });
    },
    async onSubmit() {
      // The submit button is disabled while pending, but Enter in the form still
      // reaches here — without this guard each extra submit creates a duplicate
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      try {
        const createdOpportunity = await this.addOpportunity(this.newOpportunity);
        // A create superseded by another one is cancelled service-side and resolves
        // with nothing rather than throwing: claim neither success nor failure
        if (!createdOpportunity) return;

        this.$toast.success(this.$tc('page.opportunity-details.opportunity-manipulations.add-success', 1));
        await this.uploadDocuments(createdOpportunity);

        this.closeDialog();
      } catch (error) {
        // Dialog stays open so the entered data survives and can be resubmitted
        this.$toast.error(error, {}, this.$t('page.opportunity-details.opportunity-manipulations.add-fail'));
      } finally {
        this.isSubmitting = false;
        this.isUploadingDocuments = false;
      }
    },
    async uploadDocuments(opportunity) {
      if (!this.tenantHasDocumentsAccess() || !this.documents.length) return;

      const formData = new FormData();
      this.documents.forEach((file) => {
        if (file) formData.append('file[]', file);
      });

      this.isUploadingDocuments = true;

      try {
        await this.addDocuments({ opportunity, formData });
        this.$toast.success(this.$t('page.opportunity-details.document-manipulations.add-success'));
      } catch (error) {
        this.$toast.error(
          error,
          {},
          this.$t('page.opportunity-details.document-manipulations.add-fail', { file: error.message }),
        );
      }
    },
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.resetForm();
      this.$emit('close');
    },
    onGridTemplateChange(template) {
      this.newOpportunity.evaluationGridTemplateId = template;
    },
    onSourceChange(source) {
      this.newOpportunity.source = source;
    },
    onDatesChange(dates) {
      this.dates = dates;
      this.newOpportunity.startDate = formatDate(toMoment(dates[0]), null).replace('T', ' ');
      this.newOpportunity.endDate = formatDate(toMoment(dates[1]), null).replace('T', ' ');
    },
    addFiles(files) {
      files.forEach((file) => {
        if (isXlsFile(file) && !tenantHasSlipExtractionAccess()) {
          this.$toast.warning(this.$t('page.opportunity-download.wrong-format'));
          return;
        }
        // The API rejects the whole batch when two files share a name, and both
        // the list keys and the delete lookup identify a file by its name
        if (this.documents.some((existing) => existing.name === file.name)) {
          this.$toast.warning(this.$t('error.code.DUPLICATED_DOCUMENT_NAME'));
          return;
        }
        this.documents.push(file);
      });
    },
    deleteOpportunityDocument(document) {
      this.manipulatedObject = document;
      this.$refs.dialogDeleteDocument.openDialog(document);
    },
    downloadDocument(file) {
      downloadBlob(file, file.name);
    },
    executeDeleteDocument() {
      const document = this.manipulatedObject;
      const index = this.documents.findIndex((file) => file.name === document.name);
      this.documents.splice(index, 1);
    },
    resetForm() {
      this.$validator.reset();
      this.errors.clear();
      this.$refs.dates.clearDatePickerDates();
      this.documents = [];
      this.newOpportunity = {
        tenderId: null,
        name: '',
        emitter: '',
        evaluationGridTemplateId: null,
        statusId: null,
        source: 'external',
        startDate: null,
        endDate: null,
        notes: null,
      };
    },
  },
};
</script>

<style lang="scss">
.base-dialog__add-opportunity {
  min-height: 420px;
  width: 750px;
  position: relative;
  border-radius: $border-radius-root;
  height: auto !important;
  margin: 0;
  top: auto;

  .header__title {
    margin: 0 !important;
    padding: 32px;
    width: 100%;
    height: auto !important;

    .subtitle {
      padding-top: 8px;
      font-weight: normal;
      font-size: 14px;
    }
  }

  .base-dialog__content {
    background: $color-white !important;
    padding: 0 !important;

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    .dates {
      display: flex;
      justify-content: space-between;
      gap: 16px;
    }

    .upload-title {
      font-weight: bold;
      margin-bottom: 8px;
    }
  }

  .base-dialog__actions {
    display: flex;
    justify-content: flex-end;
    padding: 32px;
    background: $color-white !important;

    form {
      display: flex;
      gap: 16px;
    }
  }

  .close-icon__container {
    display: none;
  }
}

.add-opportunity {
  &__form {
    max-height: 500px;
    padding: 0 32px;
  }

  &__document {
    margin-top: 8px;
  }

  &__upload {
  }
}

.base-dialog__header {
  height: auto !important;
}
</style>
